/**
 * EdgPanel — the React Flow EDG mounted inside the real project layout (phase 6c
 * step 3, the cutover). This REPLACES the old Rappid `components/EDG` class.
 *
 * It reproduces the old EDG's chrome and contract exactly so the page looks and
 * behaves identically (6b proved pixel parity for the canvas itself):
 *
 *  - Renders the SAME `Panel` (className `edg-view`, type `main`) with the SAME
 *    `edg-view-header` the old `EDG.renderHeader` produced: the "EDG" title, the
 *    tool toolbar (add-object / add-dependency+dropdown / add-inheritance /
 *    show-BUL / show-TDL / layout-BUL / layout-TDL / save-image), and the close
 *    button. The toolbar + save-image button drive the SAME observable `ui.edg`
 *    store and the SAME `ui.project.setExportEDGImage` flow as before.
 *  - Routes selection / add-object / add-dependency / add-inheritance / delete
 *    through the EXACT confirmable handlers `ProjectVersion` already owns (passed
 *    as props), so the dirty-inspector guard, the add/delete modals, and the
 *    report flow are unchanged. Keyboard del/esc stay owned by ProjectVersion's
 *    global `keyboard` listeners (the old EDG had none of its own).
 *  - Commits node position/size, edge vertices, and the dagre auto-layout through
 *    the EXISTING uimodel commands — the same direct MST writes the old EDG made
 *    (so undo/redo + autosave work for free; controller-based adds keep their
 *    existing non-undoable semantics, unchanged from the baseline).
 *
 * The interaction wiring mirrors `DevEdg` (the 6c steps 1-2 dev surface); the
 * difference is that the modal-driven flows are delegated to ProjectVersion's
 * confirmable props instead of owned locally, and the toolbar/close live in the
 * Panel header instead of a floating dev bar.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import Panel from '../components/Panel';
import IconButtonJs from '../components/IconButton';
import { Close as CloseIcon } from '../components/Icons';

// IconButton is an untyped JS component; its arrow-destructured param infers as
// `Object` under allowJs, which rejects `children`. Re-type it at the boundary so
// the header can pass the close icon as a child (the props we use: onClick).
const IconButton = IconButtonJs as React.FC<{
  onClick: () => void;
  children?: React.ReactNode;
}>;
import {
  updateEDGPosition,
  updateEDGSize,
  updateEDGVertices,
  updateEDGInheritanceVertices,
  clearAllEDGVertices,
} from '../merode/commands/uimodel';
import EdgFlow from './EdgFlow';
import EdgToolbar from './EdgToolbar';
import { computeEdgLayout, type RankDir } from './edgLayout';
import type { Notation } from './ObjectTypeNode';
import type { Pt } from './edgeGeometry';

export type EdgPanelProps = {
  /** The MERODE model (a `version.content`). */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
  /** Active notation (from `project.model.notation`). */
  notation: Notation;
  /** The EDG UI store (`store.ui.edg`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any;
  /** Currently selected element id (`store.ui.selected`). */
  selected: string | null;
  /** Close the EDG panel (the old `requestClose` → `visible.toggleEDG`). */
  requestClose: () => void;
  /**
   * Class for the floating zoom toolbar, mirroring the old EDG's
   * `floatingToolbarClassName` (`''` when more than one panel is visible, else
   * `'shifted'`).
   */
  floatingToolbarClassName?: string;

  // --- ProjectVersion's confirmable handlers (the dirty-inspector guard lives
  // inside these wrappers; the add/delete modals + report flow are owned by
  // ProjectVersion). Signatures match the props the old EDG received. ---

  /** Select an element (confirmable). */
  onSelect: (id: string | null) => void;
  /** Start the add-object flow at a canvas point (confirmable). */
  onAddObject: (position: { x: number; y: number }) => void;
  /** Start the add-dependency flow (confirmable). */
  onAddDependency: (sourceId: string, targetId: string, type: string) => void;
  /** Start the add-inheritance flow (confirmable). */
  onAddInheritance: (sourceId: string, targetId: string) => void;
  /**
   * Start the PNG export (the old "Save image" button →
   * `ui.project.setExportEDGImage(true)`, consumed by ProjectActions). PNG export
   * itself is 6e; the trigger stays here so users can still launch it.
   */
  onExportImage: () => void;
};

const EdgPanel = observer(function EdgPanel({
  model,
  notation,
  ui,
  selected,
  requestClose,
  floatingToolbarClassName,
  onSelect,
  onAddObject,
  onAddDependency,
  onAddInheritance,
  onExportImage,
}: EdgPanelProps) {
  // --- Selection / pane click ------------------------------------------------
  // onSelect is ProjectVersion's confirmable('Select') wrapper. A blank-canvas
  // click deselects (the old `onPaperClick` always called onSelect(null)); the
  // add-object branch fires in EdgFlow before this when the tool is armed.
  const onPaneClick = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  // --- Position / size / vertices — the EXISTING uimodel commands (direct MST
  // writes, exactly like the old EDG's updateObjectTypePosition etc.). These are
  // EDG-internal (the old EDG committed them itself, NOT through a confirmable). -
  const onChangePosition = useCallback(
    (objectTypeId: string, x: number, y: number) => {
      if (!model || !model.uimodel) return;
      const ot = model.metamodel.getObjectTypeById(objectTypeId);
      if (ot) updateEDGPosition(model.uimodel, ot, { x, y });
    },
    [model]
  );

  const onChangeSize = useCallback(
    (objectTypeId: string, width: number, height: number) => {
      if (!model || !model.uimodel) return;
      const ot = model.metamodel.getObjectTypeById(objectTypeId);
      if (ot) updateEDGSize(model.uimodel, ot, { width, height });
    },
    [model]
  );

  const onChangeVertices = useCallback(
    (edgeId: string, kind: 'dependency' | 'inheritance', vertices: Pt[]) => {
      if (!model || !model.uimodel) return;
      const metamodel = model.metamodel;
      const uimodel = model.uimodel;
      if (kind === 'inheritance') {
        const inh = metamodel.getInheritanceById(edgeId);
        if (inh) updateEDGInheritanceVertices(uimodel, inh, vertices);
      } else {
        const dep = metamodel.getDependencyById(edgeId);
        if (dep) updateEDGVertices(uimodel, dep, vertices);
      }
    },
    [model]
  );

  // --- Dagre auto-layout (BUL/TDL buttons) — port of `EDG.autoLayout` ---------
  // Clear ALL vertices (the layout algorithm doesn't move them, so they would
  // dangle), run dagre with the old constants, then commit every node position
  // through the existing `updateEDGPosition` command. Each commit is its own MST
  // action, so the TimeTraveller records one history entry per node — the SAME
  // per-node undo granularity the old JointJS `change:position → updateEDGPosition`
  // produced. (We do NOT wrap in a mobx transaction — that makes MST treat the
  // inner actions as nested and record nothing, breaking layout undo.)
  const onLayout = useCallback(
    (direction: RankDir) => {
      if (!model || !model.uimodel) return;
      const metamodel = model.metamodel;
      const uimodel = model.uimodel;
      const uiEdg = uimodel.edg;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const layoutNodes = metamodel.objectTypes.map((ot: any) => {
        const node = uiEdg.findNode(ot);
        return {
          id: ot.id,
          width: node ? node.size.width : 100,
          height: node ? node.size.height : 50,
        };
      });
      const layoutEdges: { source: string; target: string }[] = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metamodel.dependencies.forEach((dep: any) => {
        layoutEdges.push({ source: dep.master.id, target: dep.dependent.id });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      metamodel.inheritances.forEach((inh: any) => {
        layoutEdges.push({ source: inh.supertype.id, target: inh.subtype.id });
      });

      const positions = computeEdgLayout(layoutNodes, layoutEdges, direction);

      clearAllEDGVertices(uimodel);
      for (const pos of positions) {
        const ot = metamodel.getObjectTypeById(pos.id);
        if (ot) updateEDGPosition(uimodel, ot, { x: pos.x, y: pos.y });
      }
    },
    [model]
  );

  // --- Header — reproduces `EDG.renderHeader` (the SAME `edg-view-header`
  // markup the old EDG used: title, tool toolbar incl. save-image, close). The
  // header is a render-prop the Panel calls; EdgToolbar/IconButton are observers
  // so they track the `ui` store and `requestClose` identity themselves. --------
  const renderHeader = useCallback(
    () => (
      <div className="edg-view-header h-full flex justify-center items-center pl-[15px]">
        <div className="title mr-[15px]" title="Existence Dependency Graph">
          EDG
        </div>
        <EdgToolbar
          ui={ui}
          onLayout={onLayout}
          onExportImage={onExportImage}
          className="grow mr-[15px]"
        />
        <div className="close-wrapper">
          <IconButton onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    ),
    [ui, onLayout, onExportImage, requestClose]
  );

  return (
    <Panel
      className="edg-view grow w-[calc(50%-300px)] min-w-[300px]"
      type="main"
      header={renderHeader}
    >
      <div className="edg-flow">
        <EdgFlow
          model={model}
          notation={notation}
          ui={ui}
          interactive
          selectedId={selected}
          onSelect={onSelect}
          onPaneClick={onPaneClick}
          onAddObject={onAddObject}
          onAddDependency={onAddDependency}
          onAddInheritance={onAddInheritance}
          onChangePosition={onChangePosition}
          onChangeSize={onChangeSize}
          onChangeVertices={onChangeVertices}
          floatingToolbarClassName={floatingToolbarClassName}
        />
      </div>
    </Panel>
  );
});

export default EdgPanel;
